import React, {createContext, FC, useContext, useEffect, useMemo} from "react";
import {AccountType, Locale, Scalars} from "../../.cache/__types__";
import {useSession} from "next-auth/react";
import {useAuthorizedUserLazyQuery} from "@gql/query/user/authUser.gql";
import {propertyRequiredArray} from "../types/general";
import * as Sentry from '@sentry/nextjs';

export type User = {
    id: Scalars["ID"];
    firstname: string;
    lastname: string;
    accountType: AccountType;
    timeAccountExpiration?: number | null;
    email: string;
    phone?: string | null;
    photo?: string | null;
    locale: Locale;
    isOwner: boolean;
    canAddWatchdog: boolean;
}

const UserContext = createContext<{user: User | null, refetch: () => any}>({user: null, refetch: () => {}});

export const useUser = () => {
    return useContext(UserContext);
};

export const UserProvider: FC = ({children}) => {
    const {status, data} = useSession();

    const [getAuthData, {data: authData, refetch}] = useAuthorizedUserLazyQuery({fetchPolicy: "network-only"});

    const user = useMemo(() => {
        if (authData?.authUser) {
            return propertyRequiredArray([authData.authUser], ["id", "firstname", "lastname", "email", "accountType", "locale", "isOwner", "canAddWatchdog"])?.at(0) ?? null;
        }
        return null;
    }, [authData?.authUser]);

    useEffect(() => {
        if (user) {
            Sentry.setUser({
                id: user.id,
                email: user.email,
            });
        } else {
            Sentry.configureScope(scope => scope.setUser(null));
        }
    }, [user]);

    useEffect(() => {
        getAuthData();
    }, [status, data?.user?.id, getAuthData]);

    return (
        <UserContext.Provider value={{user, refetch}}>
            {children}
        </UserContext.Provider>
    );
}
